import cx from 'classnames'
import { KeyboardEvent, MouseEvent, ReactNode, useCallback } from 'react'
import { SanityContentBlockLinkPage } from '@data/sanity/queries/types/content'
import {
  SanityButtonStyle,
  SanityFontCase,
} from '@data/sanity/queries/types/link'
import Button, {
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  getButtonColor,
  getButtonIconAlignment,
  getButtonSize,
  getButtonVariant,
} from '@components/buttons/button'
import { useOpenCasePage } from '@lib/link'
import LinkFrame from './frame'

interface CasePageLinkProps {
  casePage: SanityContentBlockLinkPage
  id?: string
  tabIndex?: number
  'aria-label'?: string
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
  buttonColor?: ButtonColor
  buttonStyles?: SanityButtonStyle
  isButton?: boolean
  onClick?: () => void
  fontCase?: SanityFontCase
  className?: string
  children?: ReactNode
}

const CasePageLink = ({
  casePage,
  id,
  tabIndex,
  'aria-label': ariaLabel,
  buttonVariant,
  buttonSize,
  buttonColor,
  buttonStyles,
  isButton,
  onClick,
  fontCase,
  className,
  children,
}: CasePageLinkProps) => {
  const openCasePage = useOpenCasePage()

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()

      openCasePage(casePage.slug ?? '')

      if (onClick) {
        onClick()
      }
    },
    [casePage, onClick, openCasePage],
  )
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      event.preventDefault()

      openCasePage(casePage.slug ?? '')

      if (onClick) {
        onClick()
      }
    },
    [casePage, onClick, openCasePage],
  )

  const casePageLinkButton = (
    <Button
      id={id}
      role="link"
      tabIndex={tabIndex}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={ariaLabel}
      variant={buttonVariant ?? getButtonVariant(buttonStyles?.variant)}
      size={buttonSize ?? getButtonSize(buttonStyles?.size)}
      color={buttonColor ?? getButtonColor(buttonStyles?.color)}
      icon={buttonStyles?.icon}
      iconAlignment={getButtonIconAlignment(buttonStyles?.iconAlignment)}
      className={cx(
        {
          btn: isButton,
          'w-full': buttonStyles?.isFullWidth,
        },
        fontCase ?? '',
        className,
      )}
    >
      {children}
    </Button>
  )

  return buttonStyles?.frame ? (
    <LinkFrame buttonStyles={buttonStyles}>{casePageLinkButton}</LinkFrame>
  ) : (
    casePageLinkButton
  )
}

export default CasePageLink
